<template>
    <div ref="multiSelectRef"
         data-component="MultiSelect"
         class="multi-select"
         @focusout="handleFocusOutside">
        <div ref="multiSelectRefField"
             class="multi-select__field flex-grow form-control input-lg">
            <span v-if="selectedOptionsCount > 0"
                  class="label label-neutral"
                  @click.stop="toggleDropdown(false)">
                {{ l('FORMS.MULTI_SELECT--SELECTED', { count: selectedOptionsCount }) }}
            </span>
            <input ref="multiSelectRefInput"
                   v-model="searchQuery"
                   :name="name"
                   class="multi-select__field-search"
                   :placeholder="l(placeholder)"
                   @click.stop="inputClicked"
                   @focus="$event.target.select()"
                   @keydown="inputKeydown" />
            <span class="multi-select__field-chevron"
                  @click.stop="toggleDropdown">
                <i :class="{'fa-chevron-up': isOpen, 'fa-chevron-down': !isOpen}"
                   class="fa"></i>
            </span>
        </div>

        <div class="multi-select__selected-options flex">
            <span v-for="option in selectedOptions"
                  :key="option.ID"
                  class="label label-neutral label-lg multi-select__selected-options-option clickable"
                  @click.stop="toggleOption(option)">
                <i class="fa-solid fa-circle-xmark"></i>
                {{ option.name }}
            </span>
        </div>

        <!-- Dropdown -->
        <!-- The tabindex is needed so that the dropdown can be focused when a click happens inside it and the focusout
        event on .multi-select won't dismiss it when clicked. -->
        <div v-if="isOpen"
             ref="multiSelectDropdown"
             class="multi-select__dropdown"
             tabindex="-1">
            <div v-if="totalVisibleOptions > 0"
                 class="multi-select__dropdown-body">
                <template v-for="(groupOptions, group) in filteredGroupedOptions"
                          :key="group">
                    <div v-if="!(hideSelectedGroupNames.includes(group))"
                         class="multi-select__dropdown-optgroup">
                        {{ group }}
                    </div>
                    <template v-for="option in groupOptions"
                              :key="option.ID">
                        <div v-if="option.visible"
                             class="multi-select__dropdown-option"
                             tabindex="0"
                             @click="toggleOption(option)"
                             @keydown.space.prevent="toggleOption(option)"
                             @keydown.enter.stop="toggleDropdown">
                            <span class="multi-select__dropdown-checkbox"
                                  :class="{ 'multi-select__dropdown-checkbox--selected': option.selected }">
                                <!-- Duplicate @click prevents weird behaviour closing the dropdown when deselecting -->
                                <i v-if="option.selected"
                                   class="fa fa-check"
                                   @click.stop="toggleOption(option)"></i>
                            </span>
                            <span class="multi-select__dropdown-option-label">
                                {{ option.name }}
                            </span>
                        </div>
                    </template>
                    <div v-if="groupOptions.filter((o) => o.visible).length === 0">
                        <strong class="multi-select__dropdown-option-no-results">
                            {{ l('FORMS.MULTI_SELECT--NO_VISIBLE_OPTIONS') }}
                        </strong>
                    </div>
                </template>
            </div>
            <div v-else
                 class="flex--grow">
                <strong class="multi-select__dropdown-no-results">
                    {{ l(noResultsText) }}
                </strong>
            </div>
            <div class="multi-select__dropdown-footer">
                <button class="btn btn-white"
                        @click.stop="toggleDropdown">
                    {{ l('CLOSE') }}
                </button>
            </div>
        </div>
    </div>
</template>

<script>
import _ from 'lodash';
import { cloneDeep } from '@/functions/clone';
import { compareStringsForFilter, normalizeSearchQuery } from '@/functions/strings';
import { nextTick } from 'vue';
import { scrollElementInContainer } from '@/functions/dom';
import { timeoutPromise } from '@/functions/functions';

export default {
    name: 'MultiSelect',

    props: {
        // Name for this multi-select, like other <input> elements.
        name: {
            type: String,
            required: true,
        },
        initialOptions: {
            type: Object,
            required: true,
        },
        selectedOptionIds: {
            type: [Array, null],
            required: true,
        },
        placeholder: {
            type: String,
            default: 'FORMS.MULTI_SELECT--PLACEHOLDER',
        },
        noResultsText: {
            type: String,
            default: 'FORMS.MULTI_SELECT--NO_RESULTS',
        },
        closeMenu: {
            type: Boolean,
            default: false,
        },
        // An optional css selector of a scrollable parent element. If this selector is provided when clicked this
        // component will scroll so that it is at the top of the container.
        scrollableContainerSelector: {
            type: String,
            default: null,
        },
        hideFirstGroupName: {
            type: Boolean,
            default: true,
        },
        hideSelectedGroupNames: {
            type: Array,
            default: () => [],
        },
    },

    emits: ['update:selectedOptions'],

    data() {
        return {
            debounceTime: 150,
            isOpen: false,
            options: null,
            searchQuery: '',
            totalVisibleOptions: 0,
            toggleDropdown: null, // Placeholder for the debounced method.
        };
    },

    computed: {
        selectedOptions() {
            return Object.values(this.options)
                .flatMap((group) => group.filter((option) => option.selected === true));
        },
        filteredGroupedOptions() {
            return this.options;
        },
        selectedOptionsCount() {
            return this.selectedOptions.length || 0;
        },
    },

    watch: {
        /**
         * Watch the prop for updates such as the selectedOptionIDs being cleared in the parent component.
         *
         * @param {number[]} newData
         */
        selectedOptionIds(newData) {
            this.updateOptionsSetSelected(newData);
        },

        /**
         * Update visible options when the search query changes.
         */
        searchQuery() {
            this.applySearchQuery();
        },

        /**
         * If the initial options have changed after the initial load it means that the filters have been cleared.
         */
        initialOptions() {
            this.searchQuery = null;
        },
    },

    created() {
        this.toggleDropdown = _.debounce(this.debouncedToggleDropdown, this.debounceTime, {
            leading: true,
            trailing: false,
        });

        // Clone the prop, so we don't mutate it (even though you can with mutate objects if you want).
        this.options = cloneDeep(this.initialOptions);
        if (this.hideFirstGroupName) {
            this.hideSelectedGroupNames.push(Object.keys(this.options)[0]);
        }
        this.updateOptionsSetSelected(this.selectedOptionIds);
        this.updateOptionsSetVisibility();
    },

    mounted() {
        document.addEventListener('keydown', this.keydownHandler);
        // document.addEventListener('click', this.handleClickOutside);
    },

    unmounted() {
        // document.removeEventListener('click', this.handleClickOutside);
        document.removeEventListener('keydown', this.keydownHandler);
    },

    methods: {
        async debouncedToggleDropdown(closeAndClearSearchQuery = true) {
            // This means we can't close the dropdown by clicking on the input element because if we do so the UI
            // can become jerky for example filtering clicking then filtering again causes the dropdown to close
            // and then reopen which doesn't feel good.

            const scrollableContainerElement = document.querySelector(this.scrollableContainerSelector);
            const scrollSpacerClassName = 'scroll-spacer';
            const closeActions = () => {
                this.isOpen = false;
                if (scrollableContainerElement) {
                    document.querySelector(`${this.scrollableContainerSelector} .${scrollSpacerClassName}`).style.height = '0px';
                    scrollableContainerElement.style.overflow = 'auto';
                }
            };

            if (this.isOpen) {
                // You deliberately can't close the dropdown by clicking on the input.
                if (closeAndClearSearchQuery) {
                    this.searchQuery = null;
                    closeActions();
                }
            } else {
                if (this.scrollableContainerSelector) {
                    scrollableContainerElement.style.overflow = 'hidden';
                    scrollElementInContainer(this.$refs.multiSelectRef, scrollableContainerElement, scrollSpacerClassName);
                    // There is no callback on scrollEvents. We need to wait until the scroll has stopped otherwise the
                    // dropdown opens in the wrong place. We could track movement but a small pause is actually nice.
                    await timeoutPromise(500);
                }

                const bottom = this.$refs.multiSelectRefField.getBoundingClientRect().bottom;
                const width = this.$refs.multiSelectRefField.clientWidth;

                // The height or rather the drop of the dropdown is from the bottom of the triggering element
                // (i.e. the fake input element) minus 20px, so it doesn't touch the bottom of the browser window.
                const height = window.innerHeight - bottom - 20;

                this.isOpen = true;

                nextTick(() => {
                    this.$refs.multiSelectDropdown.style.top = bottom + 'px';
                    this.$refs.multiSelectDropdown.style.width = width + 'px';
                    this.$refs.multiSelectDropdown.style.height = height + 'px';
                    this.$refs.multiSelectRefInput.focus();
                });
            }
        },

        inputClicked(event) {
            event.target.select();
            this.toggleDropdown(false);
        },

        // When a user tabs into the field and begins to type.
        inputKeydown(event) {
            if (!this.isOpen) {
                if (event.key === 'Tab') {
                    // We need to be able to tab past the component without opening the dropdown unless we click or type.
                    return;
                }
                // We need to use preventDefault otherwise the scroll won't happen because the keypress prevents it.
                // However, this prevents the keystrokes from being captured, therefore, we need to capture any
                // keystrokes that happen before or during the scroll.
                event.preventDefault();

                this.toggleDropdown(false);

                if (!this.searchQuery) {
                    // We don't want null appearing the input field. The 1st keypress is always null.
                    this.searchQuery = '';
                }
                // We only want to append printable keys not things like Shift.
                if (event.key.length === 1 && event.key.match(/^[A-Z0-9!"#$%&'()*+,\-./:;<=>?@[\\\]^_`{|}~]$/i)) {
                    if (event.key === 'Backspace') {
                        this.searchQuery = this.searchQuery.slice(0, -1);
                    } else {
                        this.searchQuery += event.key;
                    }
                }
            }
        },

        toggleOption(option) {
            option.selected = !option.selected;
            this.$emit('update:selectedOptions', this.selectedOptions);
        },

        // This is now superseded by the handleFocusOutside event but I leaving it here for reference for now.
        // handleClickOutside(event) {
        //     // This closes the dropdown if the user clicks elsewhere on the screen.
        //     // This is also what closes the menu if it is open and the user clicks on a clear link or the close button.
        //     if (this.$refs.multiSelectRef && !this.$refs.multiSelectRef.contains(event.target)) {
        //         if (this.isOpen) {
        //             this.toggleDropdown();
        //         }
        //         this.searchQuery = null;
        //     }
        // },

        handleFocusOutside(event) {
            // This closes the dropdown if the dropdown or its children lose focus.
            if (this.$refs.multiSelectRef && this.$refs.multiSelectRef.contains(event.relatedTarget)) {
                // The multiSelect or a child element has focus.
                return;
            }

            // We are only interested in when focus moves to another element inside the document not if it moves
            // to somewhere else like another browser tab or the developer console.
            if (event.relatedTarget !== null) {
                this.searchQuery = null;
                if (this.isOpen) {
                    this.toggleDropdown();
                }
            }
        },

        keydownHandler(event) {
            // This closes the dropdown if the user presses the ESC key.
            if (event.key === 'Escape') {
                if (this.isOpen) {
                    this.toggleDropdown();
                }
            }
        },

        /**
         * @param {number[]} selectedOptionIds
         */
        updateOptionsSetSelected(selectedOptionIds) {
            // eslint-disable-next-line guard-for-in
            for (const group in this.options) {
                // For each option in the category
                this.options[group].forEach((option) => {
                    option.selected = selectedOptionIds && selectedOptionIds.length > 0 && selectedOptionIds.includes(option.ID);
                });
            }
        },

        /**
         * @param {{string: string, words: string[]}} normalizedSearchQuery
         */
        updateOptionsSetVisibility(normalizedSearchQuery) {
            let totalVisibleOptionsCount = 0;
            // eslint-disable-next-line guard-for-in
            for (const group in this.options) {
                // For each option in the category
                // eslint-disable-next-line no-loop-func
                this.options[group].forEach((option) => {
                    if (!normalizedSearchQuery) {
                        option.visible = true;
                    } else {
                        option.visible = compareStringsForFilter(option.name, normalizedSearchQuery);
                    }
                    if (option.visible) {
                        totalVisibleOptionsCount++;
                        option.tabindex = totalVisibleOptionsCount;
                    }
                });
            }
            this.totalVisibleOptions = totalVisibleOptionsCount;
        },

        applySearchQuery() {
            const normalizedSearchQuery = this.searchQuery ? normalizeSearchQuery(this.searchQuery) : null;
            this.updateOptionsSetVisibility(normalizedSearchQuery);
        },
    },
};

</script>

<style lang="less">
@import "../../../less/variables";

@multi-select__checkbox-height: 25px;

.multi-select {
    .label {
        i {
            color: @darker-grey;
        }
    }

    .multi-select__field {
        display: flex;
        // Allow the element to expand if the labels + placeholder need to wrap
        &.input-lg {
            min-height: 45px;
        }
        // Allow the element to expand if the labels + placeholder need to wrap
        &.form-control {
            min-height: 35px;
            height: auto;
        }
        padding: 0;

        .label {
            margin: @half-gutter-width;
            margin-right: 0;
        }

        &:focus-within {
            // These are styles applied to a real form element with focus.
            border-color: @dt-mid-blue-alt;
            outline: 0;
            -webkit-box-shadow: inset 0 1px 1px rgba(0, 0, 0, .075), 0 0 8px rgba(102, 175, 233, .6);
            box-shadow: inset 0 1px 1px rgba(0, 0, 0, .075), 0 0 8px rgba(102, 175, 233, .6);
        }
    }

    .multi-select__field-chevron {
        font-size: 10px;
        position: absolute;
        right: 4px;
        top: 26px;
        width: 15px;
        height: 43px;
        line-height: 43px;
        text-align: right;
        // The native select chevron is #555 but it's more bold.
        // Our chevron is thinner so using a darker grey makes it match better.
        color: @darkest-grey-alt;
        font-weight: 300;
    }

    .multi-select__field-search {
        min-height: 43px;
        flex-grow: 1;
        border: 0;
        outline: 0;
        text-indent: @half-gutter-width;
        background-color: transparent;
    }

    .multi-select__selected-options {
        flex-wrap: wrap;
        gap: @half-gutter-width;
        margin-top: @half-gutter-width;

        &:empty {
            display: none;
        }

        .multi-select__selected-option {
            cursor: pointer;
        }
    }

    .multi-select__dropdown {
        margin-top: @half-gutter-width;
        border: 1px solid @dark-neutral-grey;
        border-radius: @border-radius-base;
        text-align: left;
        box-shadow: 0 4px 8px 0 rgba(0,0,0, 0.15);
        position: fixed;
        z-index: 4000;
        background: white;
        display: flex;
        flex-direction: column;
    }

    .multi-select__dropdown-body {
        padding: @gutter-width;
        flex-grow: 1;
        flex-shrink: 1;
        overflow-y: auto;
    }

    .multi-select__dropdown-no-results {
        display: block;
        flex-grow: 1;
        color: @dark-neutral-grey;
        padding: @gutter-width;
    }

    .multi-select__dropdown-option-no-results {
        color: @dark-neutral-grey;
        padding: @half-gutter-width;
    }

    .multi-select__dropdown-optgroup {
        font-weight: bold;
        margin: @half-gutter-width 0;
    }

    .multi-select__dropdown-option {
        height: @multi-select__checkbox-height;

        // Vertically center the checkbox and the text.
        display: flex;
        align-items: center;
        gap: 10px;

        &:not(:last-child) {
            margin-bottom: 10px;
        }

        &:focus {
            .multi-select__dropdown-checkbox {
                outline: solid @dt-light-blue;
            }
        }
    }

    .multi-select__dropdown-footer {
        border-top: 1px solid @dt-light-grey;
        padding: @half-gutter-width;
        display: flex;
        button {
            flex-grow: 1;
        }
    }

    .multi-select__dropdown-checkbox {
        border: 1px solid @dt-light-grey;
        border-radius: @border-radius-base;
        display: flex;
        align-items: center;
        justify-content: center;
        width: @multi-select__checkbox-height;
        height: @multi-select__checkbox-height;
        flex-shrink: 0;
        flex-grow: 0;

        &--selected {
            background: @dt-light-blue;
            color: @white;
        }
    }

    .multi-select__dropdown-option-label {
        // Truncate long text.
        min-width: 0;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
    }
}
</style>
